import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createStyles, Grid, Link, makeStyles, Theme, Typography } from '@material-ui/core';
import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';
import { IAdjustmentLocalResource, IUpdateOrderAdjustment } from 'components/order/model/Order';
import { ViewBasketAwardItem } from './ViewBasketAwardItem';
import { isOrderAvailableAdjastment } from 'components/order/helpers';

interface IProps {
    availableAwards: IAdjustmentLocalResource[];
    onSelect: (awardId: string) => void;
    onUnselect: (awardId: string) => void;
    onClear: () => void;
    multi?: boolean;
    showAddMoreButton?: boolean;
    onAddMore: (award: IUpdateOrderAdjustment) => void;
    selected?: IAdjustmentLocalResource[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        action: {
            fontSize: theme.spacing(2),
            textDecoration: 'none'
        },
        link: {
            color: theme.palette.text.hint
        }
    })
);

export const ViewBasketAwardList: React.FC<IProps> = ({
    availableAwards,
    onUnselect,
    onSelect,
    onClear,
    onAddMore,
    selected,
    multi,
    showAddMoreButton
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const selectedIds = React.useMemo(() => {
        const ids = new Set();
        if (Array.isArray(selected)) {
            selected.forEach(item => ids.add(item.awardId));
        }
        return ids;
    }, [selected]);
    const handleClick = React.useCallback(
        (awardId: string) => {
            if (selectedIds.has(awardId)) {
                onUnselect(awardId);
            } else {
                onSelect(awardId);
            }
        },
        [onSelect, onUnselect, selectedIds]
    );
    const filteredAwards = React.useMemo<IAdjustmentLocalResource[]>(() => {
        if (!Array.isArray(availableAwards)) {
            return [];
        }
        if (multi && selected) {
            return [...selected, ...availableAwards.filter(item => !selectedIds.has(item.awardId))].filter(
                item => isOrderAvailableAdjastment(item)
            );
        }
        return availableAwards.filter(item => isOrderAvailableAdjastment(item));
    }, [availableAwards, multi, selected, selectedIds]);
    const renderAward = React.useCallback(
        (award: IAdjustmentLocalResource) => (
            <ViewBasketAwardItem award={award} onClick={handleClick} key={award.id || award.awardId} />
        ),
        [handleClick]
    );
    const renderMultiAward = React.useCallback(
        (award: IAdjustmentLocalResource) => (
            <ViewBasketAwardItem
                award={award}
                onClick={handleClick}
                key={award.id || award.awardId}
                selected={selectedIds.has(award.awardId)}
                multi
            />
        ),
        [handleClick, selectedIds]
    );

    const handleReset = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onClear();
        },
        [onClear]
    );
    const singleSelectedAward = React.useMemo(() => selected?.at(-1), [selected]);
    const handleAddMore = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (singleSelectedAward) {
                e.preventDefault();
                onAddMore({
                    type: singleSelectedAward.type,
                    awardId: singleSelectedAward.awardId,
                    quantity: singleSelectedAward.quantity
                });
            }
        },
        [onAddMore, singleSelectedAward]
    );
    if (filteredAwards.length > 0 || !!selected) {
        return (
            <ViewBillContentSection title={t('BILL_CHECKOUT_AVAILABLE_REWARDS')} bottomSpacing={2}>
                {multi && showAddMoreButton && filteredAwards.map(renderMultiAward)}
                {(!multi || !showAddMoreButton) && (
                    <>
                        {!singleSelectedAward
                            ? filteredAwards.map(renderAward)
                            : selected && (
                                  <ViewBasketAwardItem
                                      award={singleSelectedAward}
                                      selected
                                      onClick={onClear}
                                  />
                              )}
                        {!!singleSelectedAward && (
                            <Grid container>
                                <Grid item xs={showAddMoreButton ? 6 : 12}>
                                    <Typography align="center" className={classes.action}>
                                        <Link
                                            className={classes.link}
                                            href=""
                                            onClick={handleReset}
                                            color="inherit"
                                        >
                                            {t('BILL_CHECKOUT_SELECTED_REWARD_CLEAR_BTN')}
                                        </Link>
                                    </Typography>
                                </Grid>
                                {showAddMoreButton && (
                                    <Grid item xs={6}>
                                        <Typography align="center" className={classes.action}>
                                            <Link
                                                className={classes.link}
                                                href=""
                                                onClick={handleAddMore}
                                                color="inherit"
                                            >
                                                {t('PAT_QUICKPAY_SHOW_MORE_AWARDS')}
                                            </Link>
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </>
                )}
            </ViewBillContentSection>
        );
    }
    return null;
};
